import * as amplitude from '@amplitude/analytics-browser';
import { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { Store } from 'pullstate';
import { isMobile } from 'react-device-detect';
import { AppStore } from '../../../AppStore';
import { PuzzleStoreBase } from '../../../utils/types';
import { PuzzleNames } from '../../../utils/types';
import { addStructureDataForPuzzle } from '../../../utils/seoUtils';

export const DOMAIN = location.hostname === 'localhost' ?
  undefined : location.hostname.includes('onrender') ?
    'dailybaffle-1.onrender.com' : '.dailybaffle.com';

export enum SpecialKeys {
  Enter = 'Enter',
  Delete = 'Backspace',
  Tab = 'Tab',
  ArrowUp = 'ArrowUp',
  ArrowDown = 'ArrowDown',
  ArrowRight = 'ArrowRight',
  ArrowLeft = 'ArrowLeft',
  Space = ' ',
};

// custom hook to keep track of a variable's value from the previous render
export const usePrevious = <T>(value: T) => {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const useKeyboardInput = (handleInput: (key: string, shift: boolean) => void, solved: boolean, revealed: boolean) => {
  // while the sign-in dialog is open, its email/password fields own the keyboard. otherwise
  // what you type there also lands in the puzzle, which can display your password on screen.
  const signInDialogOpen = AppStore.useState(s => s.signInDialog.isOpen);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // bail before preventDefault so tab/space/arrows/enter still work inside the dialog
      if (signInDialogOpen) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (Object.values(SpecialKeys).includes(event.key as SpecialKeys)) event.preventDefault();
      if (solved || revealed) return;

      handleInput(event.key, event.shiftKey);
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleInput, solved, revealed, signInDialogOpen]);
};

export const useAnimationOnTrigger = (trigger: boolean, duration: number) => {
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    if (trigger) {
      setShowAnimation(true);
      setTimeout(() => {
        setShowAnimation(false);
      }, duration);
    }
  }, [trigger]);

  return showAnimation;
};

// This detects an orientation change. simply including it in a component will cause that component to re-render whenever the orientation changes, so that any logic assigning new orientation-specific CSS classes will be triggered and cause the UI to update. there's no need to actually do anything with the return value of this fn; merely calling it at all is sufficient to trigger the update.
export const useOrientationChange = () => {
  const [orientation, setOrientation] = useState(window.screen.orientation.type);

  useEffect(() => {
    const handleOrientationChange = () => {
      setOrientation(window.screen.orientation.type);
    };

    window.screen.orientation.addEventListener('change', handleOrientationChange);

    return () => {
      window.screen.orientation.removeEventListener('change', handleOrientationChange);
    };
  }, []);

  return orientation;
};

// This doesn't detect the device, only the screen width. So if you want to display
// mobile styles on desktop when the window is shrunk to a small size, use this to trigger it.
// Returns true if width is below the mobile styling threshold.
const DEFAULT_MOBILE_THRESHOLD = 768;
export const useIsMobileDimensions = (thresholdOverride?: number) => {
  const mobileThreshold = thresholdOverride ?? DEFAULT_MOBILE_THRESHOLD;
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= mobileThreshold);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= mobileThreshold);
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return isMobile;
};

// This is a little redundant with the previous hook, but it's just for the main page that changes its layout more. The other hook is just for pages that only change on mobile, whereas this one is for a page that resizes among 3 different layouts.
// The mobile threshold is different here deliberately because the games need mobile sizing sooner than the landing page grid does.
const THRESHOLD_1 = 1150;
const THRESHOLD_2 = 775;
const getLayoutFromWidth = (width: number) => {
  if (width > THRESHOLD_1) {
    return 1;
  } else if (width > THRESHOLD_2) {
    return 2;
  } else {
    return 3;
  }
};

export const useLayoutSwitcher = () => {
  const [layout, setLayout] = useState(() => getLayoutFromWidth(window.innerWidth));

  useEffect(() => {
    const handleResize = () => {
      setLayout(getLayoutFromWidth(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return layout;
};

// used to disable timer during development so that state updates aren't constantly being issued
const debugMode = false;
// The completed prop is just for morphology, which doesn't use the simple revealed/solved
// dichotomy that every other format uses, since partially revealing it marks it revealed
// before the user has necessarily finished with it.
// We need both useCompleted and completed because the first can be true, telling it to disregard
// solved/revealed, even while completed is still false.
export const usePause = (store: Store<PuzzleStoreBase>, useCompleted?: boolean, completed?: boolean) => {
  const { isPaused, isSoftPaused, solved, revealed } = store.useState();

  useEffect(() => {
    store.update(s => {
      if (isPaused || isSoftPaused) {
        clearInterval(s.timerUpdatingFn);
      } else {
        // no need to update timer anymore if the puzzle is solved/revealed
        if (useCompleted && completed) return;
        if (!useCompleted && (solved || revealed)) return;
        // but otherwise, when pause or soft-pause ends, set timer going again
        s.timerUpdatingFn = setInterval(() => {
          if (debugMode) return;
          store.update(s => {
            s.time++;
          });
        }, 1000);
      }
    });

    // this auto-pauses the timer when the user navigates away, unmounting the puzzle
    return () => {
      store.update(s => {
        clearInterval(s.timerUpdatingFn);
      });
    };
  }, [isPaused, isSoftPaused, solved, revealed, completed]);

  return isPaused;
};

export const useFirstTimeInstructions = (puzzleName: PuzzleNames, store: Store<PuzzleStoreBase>) => {
  useEffect(() => {
    const instructionsPreviouslyDismissed =
      Cookies.get(`${puzzleName}InstructionsDismissed`) === 'true';

    if (!instructionsPreviouslyDismissed) {
      store.update(s => {
        s.instructionsAreOpen = true;
        s.isSoftPaused = true;
      });
    }
  }, []);
};

export const usePageVisibilityPause = (
  store: Store<PuzzleStoreBase>,
  onSave: () => void,
) => {
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isViewingPage = document.visibilityState === 'visible';
      if (!isViewingPage) onSave();
      store.update(s => {
        // if a dialog is open, it's already manually softpaused and we don't need to do anything
        if (s.instructionsAreOpen || s.revealConfirmationIsOpen || s.statsDialogIsOpen) {
          return;
        }
        s.isSoftPaused = !isViewingPage;
      });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
};

export const useAmplitudeTracking = (puzzleName: PuzzleNames) => {
  useEffect(() => {
    try {
      amplitude.track(`${puzzleName}_opened`);
    } catch (error) { /* just in case! i don't trust their lib at all. */ }
  }, []);
};

// this hook adds an element to the <head> for schema.org structured data that helps
// bots to parse / understand the page or something like that
export const useStructuredDataEmbedding = (puzzleName: PuzzleNames | null) => {
  useEffect(() => {
    addStructureDataForPuzzle(puzzleName);
  }, []);
};

export const formatDuration = (seconds: number) => {
  // Calculate the number of days, hours, minutes, and seconds
  let days = Math.floor(seconds / (24 * 3600));
  seconds %= (24 * 3600);
  let hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  let minutes = Math.floor(seconds / 60);
  seconds = Math.floor(seconds % 60);

  // Build the formatted string
  let durationString = '';
  if (days > 0) {
    durationString += days + 'd ';
  }
  if (hours > 0) {
    durationString += hours + 'h ';
  }
  if (minutes > 0) {
    durationString += minutes + 'm ';
  }
  if (seconds > 0) {
    durationString += seconds + 's';
  }
  // Handle cases where the duration is less than 1 second
  if (durationString === '') {
    durationString = '0s';
  }

  return durationString.trim();
};

export const useWindowHeightStyle = () => {
  const [style, setStyle] = useState(window.innerHeight);

  useEffect(() => {
    function handleResize() {
      setStyle(window.innerHeight);
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return style;
};

// This makes it so a puzzle displays as solved on load if it was previously solved.
// We need this extra logic because without it, it would interrupt the animation
// of the final transform that the user inputted to complete the puzzle.
//
// So when they initially solve it, we actually continue displaying the normal puzzle post-animation,
// and only display the solved version if/when they revisit the page later that day.
//
// i.e. only display solved grid if it was already solved at time of load.
//
// The setTimeout avoids a race condition with the render thread to avoid the possibility of this
// sometimes being true by the time it renders, causing it to display the solved puzzle directly
// and skip the solved animation.
//
// Currently only used for rubigram and megarubigram.
export const useSolvedOnLoad = (solved: boolean) => {
  const wasSolvedOnLoad = useRef(solved);

  useEffect(() => {
    if (!wasSolvedOnLoad.current && solved) {
      setTimeout(() => {
        wasSolvedOnLoad.current = true;
      }, 300);
    }
  }, [solved]);

  return wasSolvedOnLoad.current || false;
};


export const useShouldFloatToolbar = (threshold: number): boolean => {
  const [shouldFloat, setShouldFloat] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const toolbar = document.querySelector('.morphologyContainer');

      const rect = toolbar!.getBoundingClientRect();
      const scrolledPast = rect.top <= threshold;

      setShouldFloat(scrolledPast);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    }
  }, [threshold]);

  return shouldFloat;
};

export const formatTime = (time: number) => {
  const numHours = Math.floor(time / 3600);
  const numSeconds = String(time % 60).padStart(2, '0');
  
  // pad the minutes with a 0 if over an hour
  if (numHours > 0) {
    const numMinutes = String(Math.floor((time % 3600) / 60))
      .padStart(2, '0');
   return `${numHours}:${numMinutes}:${numSeconds}`;
  } else {
    const numMinutes = String(Math.floor((time % 3600) / 60));
    return `${numMinutes}:${numSeconds}`;
  }
};